import { useState } from 'react';
import { exportFilename, exportRequestPath } from '../features/generation/generationApi';
import { ApiError } from '../lib/apiClient';
import { downloadApiFile } from '../lib/download';
import { getErrorMessage } from '../lib/errors';

interface ExportButtonProps {
  businessId: string;
  requestId: string;
  /** Only used for the fallback filename, if the header does not survive. */
  platform: string;
  label?: string;
  /** `primary` fills the button; rows and lists use the default outline. */
  variant?: 'primary' | 'secondary';
}

/** Message for the domain errors an export can answer with. */
function exportMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'EXPORT_NOT_READY':
        return 'Still rendering — try again shortly.';
      case 'REQUEST_NOT_COMPLETE':
        return 'There is nothing to download until this post is finished.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That download did not start. Try again.');
}

/**
 * Downloads the finished post as a zip — the chosen caption plus its imagery: a
 * carousel's slides, a generated single's one square, or nothing but the words
 * when the user brought their own picture.
 *
 * Shared by the review screen and the history list, so it owns its own busy and
 * message state: several rows can be on screen and only the one that was
 * clicked reports anything.
 *
 * Every failure here is non-fatal and nothing is lost by it. `EXPORT_NOT_READY`
 * in particular is a "not yet", not a "no": a layer edit clears a slide's
 * rendered PNG, and the re-render lands seconds later.
 */
export function ExportButton({
  businessId,
  requestId,
  platform,
  label = 'Download',
  variant = 'secondary',
}: ExportButtonProps) {
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  function handleClick() {
    if (isBusy) return;
    setMessage(null);
    setIsBusy(true);

    void (async () => {
      try {
        await downloadApiFile(
          exportRequestPath(businessId, requestId),
          exportFilename(platform, requestId),
        );
      } catch (caught) {
        setMessage(exportMessage(caught));
      } finally {
        setIsBusy(false);
      }
    })();
  }

  return (
    <span className="export">
      <button
        className={variant === 'primary' ? 'button button--primary' : 'button'}
        type="button"
        onClick={handleClick}
        disabled={isBusy}
      >
        {isBusy ? 'Preparing' : label}
      </button>
      {message ? (
        <span className="export__note" role="status">
          {message}
        </span>
      ) : null}
    </span>
  );
}
